import React, { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { useLazyAgencyAutocompleteQuery } from '../../src/slices/api';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 1;
const MAX_RESULTS = 20;

const AgencyAutocomplete = ({
  label,
  placeholder,
  value,
  onChange,
  consortiumId,
  parentAgencyId,
  disabled = false,
  onNotFound = null,
  notFoundLabel = 'agency'
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);
  const [trigger] = useLazyAgencyAutocompleteQuery();

  // Selected option display
  const selectedOption = options.find((opt) => opt.value === value);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (selectedOption) {
      setDisplayValue(selectedOption.label);
    } else if (!value) {
      setDisplayValue('');
    }
  }, [value, selectedOption]);

  // Load initial value label if we have a value but no options yet
  useEffect(() => {
    if (value && !selectedOption && (consortiumId || parentAgencyId)) {
      trigger({
        consortiumId,
        parentAgencyId,
        term: '',
        limit: MAX_RESULTS,
      }).then((result) => {
        if (result.data) {
          const mapped = result.data.map((a) => ({ value: a.id, label: a.name }));
          setOptions(mapped);
          const match = mapped.find((o) => o.value === value);
          if (match) setDisplayValue(match.label);
        }
      });
    }
  }, [value, consortiumId, parentAgencyId]);

  // Reset when parent scope changes
  useEffect(() => {
    setOptions([]);
    setQuery('');
  }, [consortiumId, parentAgencyId]);

  const doSearch = useCallback((term) => {
    if (!consortiumId && !parentAgencyId) return;

    setIsSearching(true);
    trigger({
      consortiumId,
      parentAgencyId,
      term,
      limit: MAX_RESULTS,
    }).then((result) => {
      if (result.data) {
        setOptions(result.data.map((a) => ({ value: a.id, label: a.name })));
      }
      setIsSearching(false);
    }).catch(() => {
      setIsSearching(false);
    });
  }, [consortiumId, parentAgencyId, trigger]);

  const handleInputChange = (event) => {
    const term = event.target.value;
    setQuery(term);
    setDisplayValue(term);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (term.length >= MIN_CHARS) {
      debounceRef.current = setTimeout(() => doSearch(term), DEBOUNCE_MS);
    } else if (term.length === 0) {
      // Show initial results on clear
      debounceRef.current = setTimeout(() => doSearch(''), DEBOUNCE_MS);
    }
  };

  const handleSelect = (val) => {
    onChange(val);
    const match = options.find((o) => o.value === val);
    if (match) setDisplayValue(match.label);
    setQuery('');
  };

  const handleFocus = () => {
    if (options.length === 0 && (consortiumId || parentAgencyId)) {
      doSearch(query || '');
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label
        className="text-[12px] font-normal leading-[16px] text-[#252525] mb-2"
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        {label}
      </label>

      <Combobox value={value} onChange={handleSelect} disabled={disabled}>
        <div className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <div className="relative w-full cursor-default overflow-hidden bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#49C44F] focus-within:border-transparent">
            <Combobox.Input
              className="w-full h-[56px] pl-4 pr-10 text-[14px] font-normal leading-[18px] text-[#252525]
                border-none focus:ring-0 focus:outline-none
                placeholder:text-[#6E6E6E]
                disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              displayValue={() => displayValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto
              rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5
              focus:outline-none list-none">
              {isSearching ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-500"
                  style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Searching...
                </div>
              ) : options.length === 0 && query.length >= MIN_CHARS ? (
                <div className="relative cursor-default select-none py-2 px-4"
                  style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {onNotFound ? (
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onNotFound(query); }}
                      className="text-[14px] text-[#49C44F] font-semibold hover:underline focus:outline-none"
                    >
                      {`Don't see your ${notFoundLabel}?`}
                    </button>
                  ) : (
                    <span className="text-gray-700">No results found.</span>
                  )}
                </div>
              ) : (
                options.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                        active ? 'bg-[#49C44F] text-white' : 'text-gray-900'
                      }`
                    }
                    value={option.value}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate text-[14px] ${selected ? 'font-medium' : 'font-normal'}`}
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {option.label}
                        </span>
                        {selected ? (
                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-white' : 'text-[#49C44F]'
                          }`}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

AgencyAutocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  consortiumId: PropTypes.string,
  parentAgencyId: PropTypes.string,
  disabled: PropTypes.bool,
  onNotFound: PropTypes.func,
  notFoundLabel: PropTypes.string,
};

export default AgencyAutocomplete;
